import React, { useEffect, useState, useCallback } from "react";
import toast from "react-hot-toast";
import {
  useCreateBillingCycleMutation,
  useGetAllBillingCyclesMutation,
  useUpdateBillingCycleMutation,
  useGetBillingCycleLogsByTenantIdMutation,
} from "../../../features/admin/billingCycle/billingCycleApi";
import { useFetchVendorsQuery } from "../../../features/admin/vendor/vendorApi";
import "./BillingCycle.css";

const computeExpirationDate = (paymentDate, billingMonths, graceDays) => {
  if (!paymentDate || !billingMonths) return "";
  const date = new Date(paymentDate);
  date.setMonth(date.getMonth() + Number(billingMonths));
  date.setDate(date.getDate() + Number(graceDays || 0));
  return date.toISOString().split("T")[0];
};

const StatusBadge = ({ status, daysRemaining }) => {
  const configs = {
    expired: { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", label: "Expired" },
    expiring_soon: {
      color: "#d97706",
      bg: "#fffbeb",
      border: "#fde68a",
      label: `Expiring in ${daysRemaining}d`,
    },
    active: { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", label: "Active" },
  };
  const cfg = configs[status] || configs.active;
  return (
    <span
      style={{
        background: cfg.bg,
        color: cfg.color,
        border: `1.5px solid ${cfg.border}`,
        borderRadius: 20,
        padding: "3px 12px",
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      {cfg.label}
    </span>
  );
};

const BillingCycle = () => {
  const token = sessionStorage.getItem("access_token");

  // Form state
  const [selectedTenant, setSelectedTenant] = useState("");
  const [billingMonths, setBillingMonths] = useState("");
  const [graceDays, setGraceDays] = useState(0);
  const [paymentDate, setPaymentDate] = useState("");
  const [editId, setEditId] = useState(null);

  // Records
  const [records, setRecords] = useState([]);

  // Logs Modal
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logsData, setLogsData] = useState([]);
  const [selectedLogsTenant, setSelectedLogsTenant] = useState("");

  // Email Modal State
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedEmailTenant, setSelectedEmailTenant] = useState(null);
  const [spocEmail, setSpocEmail] = useState("");
  const [csmEmail, setCsmEmail] = useState("");
  const [accountsEmail, setAccountsEmail] = useState("");
  const [existingWallet, setExistingWallet] = useState(null);
  const [fetchingEmail, setFetchingEmail] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);

  // RTK mutations
  const [createBilling, { isLoading: creating }] = useCreateBillingCycleMutation();
  const [getAllBilling, { isLoading: fetching }] = useGetAllBillingCyclesMutation();
  const [updateBilling, { isLoading: updating }] = useUpdateBillingCycleMutation();
  const [getLogs, { isLoading: fetchingLogs }] = useGetBillingCycleLogsByTenantIdMutation();

  // Vendors for dropdown
  const { data: vendorsData, isLoading: vendorsLoading } = useFetchVendorsQuery(token);

  const previewExpiry = computeExpirationDate(paymentDate, billingMonths, graceDays);

  const fetchAll = useCallback(async () => {
    try {
      const res = await getAllBilling({ token }).unwrap();
      if (res?.success) setRecords(res.body || []);
    } catch (e) {
      toast.error("Failed to load billing records");
    }
  }, [getAllBilling, token]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const resetForm = () => {
    setSelectedTenant("");
    setBillingMonths("");
    setGraceDays(0);
    setPaymentDate("");
    setEditId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTenant || !billingMonths || !paymentDate) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      if (editId) {
        const res = await updateBilling({
          token,
          id: editId,
          body: { billing_months: Number(billingMonths), grace_days: Number(graceDays), payment_date: paymentDate },
        }).unwrap();
        if (res?.success) {
          toast.success("Billing cycle updated successfully");
          resetForm();
          fetchAll();
        } else {
          toast.error(res?.message || "Update failed");
        }
      } else {
        const res = await createBilling({
          token,
          body: {
            tenant_id: Number(selectedTenant),
            billing_months: Number(billingMonths),
            grace_days: Number(graceDays),
            payment_date: paymentDate,
          },
        }).unwrap();
        if (res?.success) {
          toast.success("Billing cycle created successfully");
          resetForm();
          fetchAll();
        } else {
          toast.error(res?.message || "Creation failed");
        }
      }
    } catch (err) {
      toast.error(err?.data?.message || "Something went wrong");
    }
  };

  const handleEdit = (record) => {
    setEditId(record.id);
    setSelectedTenant(record.tenant_id);
    setBillingMonths(record.billing_months || "");
    setGraceDays(record.grace_days || 0);
    setPaymentDate(record.payment_date?.split("T")[0]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openLogs = async (tenantId) => {
    setShowLogsModal(true);
    setSelectedLogsTenant(tenantId);
    try {
      const res = await getLogs({ token, tenant_id: tenantId }).unwrap();
      if (res?.success) {
        setLogsData(res.body || []);
      }
    } catch (e) {
      toast.error("Failed to fetch logs");
    }
  };

  const openEmailModal = async (record) => {
    setShowEmailModal(true);
    setSelectedEmailTenant(record);
    setFetchingEmail(true);
    setSpocEmail("");
    setCsmEmail("");
    setAccountsEmail("");
    setExistingWallet(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/admin/tenant-wallet/${record.tenant_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data?.success && data?.body) {
        setExistingWallet(data.body);
        if (data.body.spoc_emails && data.body.spoc_emails.length > 0) {
          setSpocEmail(data.body.spoc_emails[0] || "");
          setCsmEmail(data.body.spoc_emails[1] || "");
          setAccountsEmail(data.body.spoc_emails[2] || "");
        }
      }
    } catch (e) {
      toast.error("Failed to fetch tenant wallet");
    } finally {
      setFetchingEmail(false);
    }
  };

  const handleSaveEmail = async () => {
    if (!selectedEmailTenant) return;
    setSavingEmail(true);
    try {
      let spocEmails = existingWallet?.spoc_emails || ["", "", ""];
      if (!Array.isArray(spocEmails)) {
        spocEmails = ["", "", ""];
      } else {
        spocEmails = [...spocEmails]; // clone
      }
      spocEmails[0] = spocEmail.trim(); // update spoc email
      spocEmails[1] = csmEmail.trim(); // update csm email
      spocEmails[2] = accountsEmail.trim(); // update accounts email

      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/admin/tenant-wallet/config`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          slug: selectedEmailTenant.slug || selectedEmailTenant.tenant_id || "",
        },
        body: JSON.stringify({
          tenant_id: selectedEmailTenant.tenant_id,
          tenant_name: selectedEmailTenant.tenant_name,
          spoc_emails: spocEmails,
          threshold_balance: existingWallet?.threshold_balance !== undefined ? existingWallet.threshold_balance : null,
        }),
      });
      const data = await response.json();
      if (data?.success) {
        toast.success("Email saved successfully");
        setShowEmailModal(false);
      } else {
        toast.error(data?.message || "Failed to save email");
      }
    } catch (e) {
      toast.error("Failed to save email");
    } finally {
      setSavingEmail(false);
    }
  };

  const tenants = vendorsData?.body || [];

  return (
    <div className="bc-page">
      <div className="bc-header">
        <div>
          <h1 className="bc-title">Billing Cycle Management</h1>
          <p className="bc-subtitle">
            Manage subscription billing cycles and expiry dates for tenants
          </p>
        </div>
        {editId && (
          <button className="bc-cancel-btn" onClick={resetForm}>
            ✕ Cancel Edit
          </button>
        )}
      </div>

      {/* ─── Form Card ─────────────────────────────── */}
      <div className="bc-card">
        <div className="bc-card-header">
          <span className="bc-card-icon">{editId ? "✏️" : "➕"}</span>
          <span className="bc-card-title">
            {editId ? "Update Billing Cycle" : "Add New Billing Cycle"}
          </span>
        </div>
        <form onSubmit={handleSubmit} className="bc-form">
          <div className="bc-form-grid">
            <div className="bc-field">
              <label className="bc-label">
                Tenant <span className="bc-required">*</span>
              </label>
              <select
                className="bc-select"
                value={selectedTenant}
                onChange={(e) => setSelectedTenant(e.target.value)}
                disabled={!!editId}
                required
              >
                <option value="">-- Select Tenant --</option>
                {tenants.map((t) => (
                  <option key={t.tenant_id} value={t.tenant_id}>
                    {t.name} (ID: {t.tenant_id})
                  </option>
                ))}
              </select>
            </div>

            <div className="bc-field">
              <label className="bc-label">
                Duration (Months) <span className="bc-required">*</span>
              </label>
              <input
                type="number"
                min="1"
                className="bc-input"
                value={billingMonths}
                onChange={(e) => setBillingMonths(e.target.value)}
                placeholder="e.g. 12"
                required
              />
            </div>

            <div className="bc-field">
              <label className="bc-label">
                Grace Period (Days)
              </label>
              <input
                type="number"
                min="0"
                className="bc-input"
                value={graceDays}
                onChange={(e) => setGraceDays(e.target.value)}
                placeholder="e.g. 15"
              />
            </div>

            <div className="bc-field">
              <label className="bc-label">
                Payment Date <span className="bc-required">*</span>
              </label>
              <input
                type="date"
                className="bc-input"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                required
              />
            </div>

            <div className="bc-field">
              <label className="bc-label">Expiration Date (Auto)</label>
              <div className="bc-expiry-display">
                {previewExpiry ? (
                  <>
                    <span className="bc-expiry-icon">📅</span>
                    <span className="bc-expiry-date">{previewExpiry}</span>
                  </>
                ) : (
                  <span className="bc-expiry-placeholder">
                    Select cycle &amp; date to preview
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="bc-form-footer">
            <button
              type="submit"
              className="bc-submit-btn"
              disabled={creating || updating}
            >
              {creating || updating ? (
                <span className="bc-spinner" />
              ) : editId ? (
                "Update Billing Cycle"
              ) : (
                "Save Billing Cycle"
              )}
            </button>
          </div>
        </form>
      </div>

      {/* ─── Listing Table ───────────────────────────── */}
      <div className="bc-card bc-table-card">
        <div className="bc-card-header">
          <span className="bc-card-icon">📋</span>
          <span className="bc-card-title">Billing Records</span>
          <span className="bc-record-count">{records.length} records</span>
        </div>

        {fetching ? (
          <div className="bc-loading">
            <div className="bc-spinner bc-spinner-lg" />
            <p>Loading records…</p>
          </div>
        ) : records.length === 0 ? (
          <div className="bc-empty">
            <span className="bc-empty-icon">💳</span>
            <p>No billing cycle records found.</p>
            <p className="bc-empty-sub">Add a billing cycle using the form above.</p>
          </div>
        ) : (
          <div className="bc-table-wrapper">
            <table className="bc-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Tenant</th>
                  <th>Months</th>
                  <th>Grace</th>
                  <th>Payment Date</th>
                  <th>Expiration Date</th>
                  <th>Status</th>
                  <th>Updated At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.map((rec, idx) => {
                  const expDate = new Date(rec.expiration_date);
                  const daysRemaining = Math.ceil(
                    (expDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
                  );
                  const status =
                    daysRemaining <= 0
                      ? "expired"
                      : daysRemaining <= 15
                      ? "expiring_soon"
                      : "active";

                  return (
                    <tr
                      key={rec.id}
                      className={
                        status === "expired"
                          ? "bc-row-expired"
                          : status === "expiring_soon"
                          ? "bc-row-warning"
                          : ""
                      }
                    >
                      <td className="bc-td-num">{idx + 1}</td>
                      <td>
                        <div className="bc-tenant-name">{rec.tenant_name || "—"}</div>
                        <div className="bc-tenant-id">ID: {rec.tenant_id}</div>
                      </td>
                      <td>
                        <span className="bc-cycle-badge">
                          {rec.billing_months} {rec.billing_months === 1 ? "Month" : "Months"}
                        </span>
                      </td>
                      <td>
                        {rec.grace_days ? `${rec.grace_days} Days` : "None"}
                      </td>
                      <td>{rec.payment_date?.split("T")[0] || "—"}</td>
                      <td>
                        <strong>{rec.expiration_date?.split("T")[0] || "—"}</strong>
                      </td>
                      <td>
                        <StatusBadge status={status} daysRemaining={daysRemaining} />
                      </td>
                      <td>
                        {rec.updated_at
                          ? new Date(rec.updated_at).toLocaleDateString("en-IN")
                          : "—"}
                      </td>
                      <td style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        <button
                          className="bc-edit-btn"
                          onClick={() => handleEdit(rec)}
                        >
                          Edit
                        </button>
                        <button
                          className="bc-edit-btn"
                          onClick={() => openEmailModal(rec)}
                          style={{ background: "#e0e7ff", color: "#3730a3", borderColor: "#c7d2fe" }}
                        >
                          Email
                        </button>
                        <button
                          className="bc-edit-btn"
                          onClick={() => openLogs(rec.tenant_id)}
                          style={{ background: "#f3f4f6", color: "#4b5563", borderColor: "#d1d5db" }}
                        >
                          Logs
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Email Modal ─────────────────────────────────────── */}
      {showEmailModal && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(15,23,42,0.55)", zIndex: 9999,
            display: "flex", alignItems: "center", justifyContent: "center",
            backdropFilter: "blur(3px)",
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowEmailModal(false); }}
        >
          <div
            style={{
              background: "#fff", borderRadius: 16, width: "90%", maxWidth: 500,
              display: "flex", flexDirection: "column",
              boxShadow: "0 24px 64px rgba(15,23,42,0.2)", overflow: "hidden",
              fontFamily: "'Inter','Segoe UI',sans-serif",
            }}
          >
            {/* Modal Header */}
            <div style={{
              background: "linear-gradient(135deg,#3730a3,#4f46e5)",
              padding: "20px 28px", display: "flex", alignItems: "center",
              justifyContent: "space-between",
            }}>
              <div>
                <h2 style={{ color: "#fff", margin: 0, fontSize: 18, fontWeight: 700 }}>📧 Config Emails</h2>
                <p style={{ color: "#e0e7ff", margin: "4px 0 0", fontSize: 13 }}>
                  For Tenant: {selectedEmailTenant?.tenant_name || selectedEmailTenant?.tenant_id}
                </p>
              </div>
              <button
                onClick={() => setShowEmailModal(false)}
                style={{
                  background: "rgba(255,255,255,0.15)", border: "none", color: "#fff",
                  borderRadius: 8, width: 34, height: 34, fontSize: 18,
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >✕</button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "28px", flex: 1 }}>
              {fetchingEmail ? (
                <div style={{ textAlign: "center", padding: "40px 20px", color: "#94a3b8" }}>
                  <div className="bc-spinner bc-spinner-lg" style={{ borderColor: "rgba(79,70,229,0.2)", borderTopColor: "#4f46e5", margin: "0 auto 16px" }} />
                  <p style={{ margin: 0, fontSize: 14 }}>Fetching current email…</p>
                </div>
              ) : (
                <div>
                  <div className="bc-field">
                    <label className="bc-label">
                      Company SPOC Email
                    </label>
                    <input
                      type="email"
                      className="bc-input"
                      value={spocEmail}
                      onChange={(e) => setSpocEmail(e.target.value)}
                      placeholder="e.g. contact@company.com"
                    />
                  </div>
                  <div className="bc-field">
                    <label className="bc-label">
                      CSM Email
                    </label>
                    <input
                      type="email"
                      className="bc-input"
                      value={csmEmail}
                      onChange={(e) => setCsmEmail(e.target.value)}
                      placeholder="e.g. csm@company.com"
                    />
                  </div>
                  <div className="bc-field">
                    <label className="bc-label">
                      Accounts Team Email
                    </label>
                    <input
                      type="email"
                      className="bc-input"
                      value={accountsEmail}
                      onChange={(e) => setAccountsEmail(e.target.value)}
                      placeholder="e.g. accounts@company.com"
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "24px" }}>
                    <button
                      className="bc-cancel-btn"
                      style={{ marginRight: "12px", border: "1px solid #e2e8f0", background: "#fff", color: "#64748b" }}
                      onClick={() => setShowEmailModal(false)}
                      disabled={savingEmail}
                    >
                      Cancel
                    </button>
                    <button
                      className="bc-submit-btn"
                      style={{ padding: "10px 20px", fontSize: "14px", width: "auto" }}
                      onClick={handleSaveEmail}
                      disabled={savingEmail}
                    >
                      {savingEmail ? <span className="bc-spinner" /> : "Save Email"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Audit Log Modal ─────────────────────────────────────── */}
      {showLogsModal && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(15,23,42,0.55)", zIndex: 9999,
            display: "flex", alignItems: "center", justifyContent: "center",
            backdropFilter: "blur(3px)",
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowLogsModal(false); }}
        >
          <div
            style={{
              background: "#fff", borderRadius: 16, width: "90%", maxWidth: 850,
              maxHeight: "85vh", display: "flex", flexDirection: "column",
              boxShadow: "0 24px 64px rgba(15,23,42,0.2)", overflow: "hidden",
              fontFamily: "'Inter','Segoe UI',sans-serif",
            }}
          >
            {/* Modal Header */}
            <div style={{
              background: "linear-gradient(135deg,#1e3a8a,#2563eb)",
              padding: "20px 28px", display: "flex", alignItems: "center",
              justifyContent: "space-between",
            }}>
              <div>
                <h2 style={{ color: "#fff", margin: 0, fontSize: 18, fontWeight: 700 }}>📋 Billing Audit Log</h2>
                <p style={{ color: "#bfdbfe", margin: "4px 0 0", fontSize: 13 }}>
                  Showing history for Tenant ID: {selectedLogsTenant}
                </p>
              </div>
              <button
                onClick={() => setShowLogsModal(false)}
                style={{
                  background: "rgba(255,255,255,0.15)", border: "none", color: "#fff",
                  borderRadius: 8, width: 34, height: 34, fontSize: 18,
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >✕</button>
            </div>

            {/* Modal Body */}
            <div style={{ overflowY: "auto", flex: 1 }}>
              {fetchingLogs ? (
                <div style={{ textAlign: "center", padding: "60px 20px", color: "#94a3b8" }}>
                  <div className="bc-spinner bc-spinner-lg" style={{ borderColor: "rgba(59,130,246,0.2)", borderTopColor: "#3b82f6", margin: "0 auto 16px" }} />
                  <p style={{ margin: 0, fontSize: 14 }}>Loading audit logs…</p>
                </div>
              ) : logsData.length === 0 ? (
                <div style={{ textAlign: "center", padding: "60px 20px", color: "#94a3b8" }}>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
                  <p style={{ margin: 0, fontSize: 15, color: "#475569", fontWeight: 600 }}>No logs found</p>
                </div>
              ) : (
                <table className="bc-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Action</th>
                      <th>Months</th>
                      <th>Grace Days</th>
                      <th>Expiration</th>
                      <th>Performed By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logsData.map((log) => {
                      const newVals = log.new_values ? (typeof log.new_values === 'string' ? JSON.parse(log.new_values) : log.new_values) : {};
                      return (
                        <tr key={log.id}>
                          <td>{new Date(log.performed_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</td>
                          <td>
                            <span style={{ 
                                display: "inline-block", padding: "3px 10px", borderRadius: "20px", 
                                fontSize: "11px", fontWeight: "700", textTransform: "uppercase",
                                background: log.action === 'created' ? "#dcfce7" : "#e0e7ff",
                                color: log.action === 'created' ? "#166534" : "#3730a3"
                            }}>
                                {log.action}
                            </span>
                          </td>
                          <td>{newVals.billing_months || "—"}</td>
                          <td>{newVals.grace_days !== undefined ? newVals.grace_days : "—"}</td>
                          <td>{newVals.expiration_date?.split("T")[0] || "—"}</td>
                          <td>{log.performed_by_name || log.performed_by || "System"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingCycle;
